/* eslint-disable prettier/prettier */
import { observable, runInAction } from 'mobx';
import { getVolumeSizeRanges } from '../../../helpers/api';

// Cache for volume size ranges fetched from backend.
//
// This is observable rather than a plain variable so that a component which renders before the
// fetch has landed re-renders once it has. getRootVolumeSizeRange is synchronous and is read
// while a form is being constructed, so without this a caller that arrives early would be stuck
// with the fallback range for the life of the component. See volumeSizeRangesLoaded below.
const _state = observable({ cache: null }, { cache: observable.ref });
let _fetchPromise = null;

const DEFAULT_RANGE = { min: 96, max: 1536, default: 96 };

/**
 * Whether a value is a usable volume size range.
 * @param {*} value
 * @returns {boolean}
 */
function isRange(value) {
  return (
    !!value &&
    typeof value === 'object' &&
    Number.isFinite(value.min) &&
    Number.isFinite(value.max)
  );
}

/**
 * Coerce whatever the API returned into a cache entry, or null if it isn't usable.
 *
 * The cache is only ever null (not fetched) or a well formed object, so that
 * volumeSizeRangesLoaded is a truthful answer and getRootVolumeSizeRange can index into
 * .ranges without checking it. An empty or half built response would otherwise either open the
 * gate on an unusable cache, which puts every workspace type back on the fallback minimum, or
 * throw while reading .ranges of undefined in the middle of building a form.
 *
 * @param {*} payload
 * @returns {{ ranges: object, default: object }|null}
 */
function toCacheEntry(payload) {
  if (!payload || typeof payload !== 'object') return null;
  if (!payload.ranges || typeof payload.ranges !== 'object') return null;
  return {
    ranges: payload.ranges,
    default: isRange(payload.default) ? payload.default : { ...DEFAULT_RANGE },
  };
}

/**
 * Load volume size ranges from the backend.
 * Call this once during app initialization or before the setup wizard is used.
 * @returns {Promise<void>}
 */
export async function loadVolumeSizeRanges() {
  if (_state.cache) return;
  if (_fetchPromise) {
    await _fetchPromise;
    return;
  }
  _fetchPromise = (async () => {
    let entry = null;
    try {
      entry = toCacheEntry(await getVolumeSizeRanges());
      if (!entry) {
        console.warn('Volume size ranges response from backend was not usable, using defaults');
      }
    } catch (error) {
      console.warn('Failed to load volume size ranges from backend, using defaults', error);
    }
    // Always settle on something usable, so callers gated on volumeSizeRangesLoaded are not left
    // waiting forever when the backend is unreachable or returns something unexpected.
    runInAction(() => {
      _state.cache = entry || { ranges: {}, default: { ...DEFAULT_RANGE } };
    });
  })();
  await _fetchPromise;
}

/**
 * Whether the volume size ranges have been fetched from the backend yet.
 *
 * Reads an observable, so an observer component that calls this in render will re-render when
 * the ranges arrive. Gate anything that reads getRootVolumeSizeRange on this: until it returns
 * true that function can only report the generic fallback range, which silently allows volumes
 * below the minimum for workspace types whose minimum is higher than the fallback's.
 *
 * @returns {boolean}
 */
export function volumeSizeRangesLoaded() {
  return _state.cache !== null;
}

/**
 * Get root volume size range for a workspace type.
 * Returns cached backend values if available, otherwise returns default.
 *
 * IMPORTANT: Call loadVolumeSizeRanges() and wait for volumeSizeRangesLoaded() before using
 * this function. The fallback it returns when the cache is cold is not a safe substitute for a
 * real range, it is only a last resort for when the backend can't be reached.
 *
 * @param {string} typeName - Workspace type name (e.g., "Windows - Lumivero NVivo")
 * @returns {{ min: number, max: number, default: number }}
 */
export function getRootVolumeSizeRange(typeName) {
  const cache = _state.cache;
  if (!cache) {
    // Nothing recoverable to do here, the caller needed the value synchronously. Log it so that a
    // missed gate shows up as something other than a silently wrong minimum.
    console.warn(
      `getRootVolumeSizeRange("${typeName}") was called before the ranges were loaded, falling back to the default range`,
    );
    return { ...DEFAULT_RANGE };
  }
  const range = cache.ranges[typeName];
  if (isRange(range)) return range;
  return cache.default;
}

export const allAvailableCategories = {
// CPU
  'Basic': { color: 'red', desc: 'Based on AWS burstable technology, these workspaces provide you with hardware designed for applications with moderate to low CPU usage that experience temporary spikes in use.' },
  'General': { color: 'blue', desc: 'These workspaces are balanced for compute and memory, and are designed for computations. They have a CPU to memory (RAM) ratio of 1CPU:8GB.' },
  'Compute Optimised': { color: 'orange', desc: 'These workspaces are designed for compute-intensive workloads. They have a CPU to memory (RAM) ratio of 1CPU:4GB.' },
  'Balanced': { color: 'blue', desc: 'These workspaces are balanced for compute and memory, and are designed for computations. They have a CPU to memory (RAM) ratio of 1CPU:8GB. Flex variants offer the same capabilities at a lower cost, providing 40% baseline CPU performance with ability to burst to 100% when needed.' },
  'Memory Optimised': { color: 'green', desc: 'These workspaces are designed for memory-intensive workloads. They have a CPU to memory (RAM) ratio of 1CPU:16GB.' },
  'High Memory': { color: 'green', desc: 'These workspaces are designed for memory-intensive workloads. They have a CPU to memory (RAM) ratio of 1CPU:16GB.' },
// GPU
  'T4': { color: 'purple', desc: 'These workspaces are powered by NVIDIA T4 GPUs.' },
  'A10G': { color: 'brown', desc: 'These workspaces are powered by NVIDIA A10G GPUs.' },
  'L4': { color: 'teal', desc: 'These workspaces are powered by NVIDIA L4 GPUs.' },
  'RTX4500': { color: 'olive', desc: 'These workspaces are powered by NVIDIA RTX PRO™ 4500 Blackwell Server Edition GPUs.' },
  'RTX6000': { color: 'olive', desc: 'These workspaces are powered by NVIDIA RTX PRO™ 6000 Blackwell Server Edition GPUs.' },
  'V100': { color: 'olive', desc: 'These workspaces are powered by NVIDIA V100 GPUs.' },
  'A100': { color: 'olive', desc: 'These workspaces are powered by NVIDIA A100 GPUs.' },
  'H100': { color: 'olive', desc: 'These workspaces are powered by NVIDIA H100 GPUs.' },
  'H200': { color: 'olive', desc: 'These workspaces are powered by NVIDIA H200 GPUs.' },
  'B200': { color: 'olive', desc: 'These workspaces are powered by NVIDIA Blackwell B200 GPUs.' },
  'B300': { color: 'olive', desc: 'These workspaces are powered by NVIDIA Blackwell Ultra B300 GPUs.' },
  'GB200': { color: 'olive', desc: 'These workspaces are powered by NVIDIA Blackwell GB200 GPUs in an UltraServer configuration.' },
  'GB300': { color: 'olive', desc: 'These workspaces are powered by NVIDIA Blackwell Ultra GB300 GPUs in an UltraServer configuration.' },
};

/**
 * The category tab a configuration belongs on, found by looking for a category name in the part of
 * the config name before " - " (for example "G20 GB200 - 4 GPU" gives "G20 GB200").
 *
 * Categories are tried in the order they are listed above, except that a category whose name sits
 * inside another matching category's name gives way to it. "GB200" contains "B200", which is listed
 * first, so without that rule every GB200 config would land on the B200 tab.
 *
 * @param {string} name The env type config name
 * @returns {string} A key of allAvailableCategories, or 'Other'
 */
export function getConfigCategory(name) {
  const prefix = String(name || '').split(' - ')[0];
  const matches = Object.keys(allAvailableCategories).filter(key => prefix.includes(key));
  const category = matches.find(key => !matches.some(other => other !== key && other.includes(key)));
  return category || 'Other';
}
